import { CreditScore } from '@/catalog/types/credit-score';
import FilterCreditDrawerDialog from '../filter-credit-drawer-dialog';
import { AgeRange } from '@/catalog/types/age-range';
import FilterPlanDrawerDialog from '../filter-plan-drawer-dialog';
import { useFilterInsuranceTier } from '@/common/hooks/use-filter-insurance-tier';
import Insurance from '@/icons/insurance';
import FilterPillSelect from '../header/filter-pill-select';

export interface PlanAgeCreditPillsProps {
  creditScores: CreditScore[];
  selectedCreditScoreId?: string;
  ageRanges?: AgeRange[];
  selectedAgeRangeId?: string;
  selectedAge: boolean;
  ageRangePlaceholder?: string;
  creditScorePlaceholder?: string;
  canViewSwap?: boolean;
  canLegacySwap?: boolean;
  selectedPlanMileage?: number;
  mileagePlans: number[];
  planHandleClose: () => void;
  planOpen: boolean;
  planHandleOpen: () => void;
  planError?: string;
  creditHandleClose: () => void;
  creditOpen: boolean;
  creditHandleOpen: () => void;
  creditError?: string;
  hideCreditModal: boolean;
  fullstoryEventDelegate: (mileage: string) => void;
  pillClassName?: string;
  alreadyQualified?: boolean;
  insurancePlanTierOptions?: string[];
  insurancePlanTierError?: string;
  selectedInsuranceTier?: string;
}

export default function PlanAgeCreditPills({
  selectedPlanMileage,
  selectedCreditScoreId,
  selectedAgeRangeId,
  selectedAge,
  creditScores,
  ageRanges,
  ageRangePlaceholder,
  creditScorePlaceholder,
  canViewSwap = false,
  canLegacySwap = false,
  mileagePlans,
  planHandleClose,
  planOpen,
  planHandleOpen,
  planError,
  creditOpen,
  creditHandleOpen,
  creditHandleClose,
  creditError,
  hideCreditModal,
  fullstoryEventDelegate,
  pillClassName,
  alreadyQualified,
  insurancePlanTierError,
  selectedInsuranceTier: selectedInsuranceTierFromSession,
  insurancePlanTierOptions,
}: PlanAgeCreditPillsProps) {
  /**
   * Legacy swaps can see plan filters. However if not legacy, use the normal show/hide where swaps cannot filter plans */
  const showPlanFilter = (() => {
    if (canLegacySwap) {
      return true;
    }

    return !canViewSwap;
  })();

  // In swap flow, users can't change their insurance tier so we shouldn't show the pill for it.
  const showInsuranceFilter =
    !canViewSwap && insurancePlanTierOptions && insurancePlanTierOptions.length > 0;

  const { selectedInsuranceTier, options, submitInsuranceTier } = useFilterInsuranceTier({
    insurancePlanTierError,
    selectedInsuranceTier: selectedInsuranceTierFromSession,
    insurancePlanTierOptions,
  });

  return (
    <>
      {!canViewSwap && !hideCreditModal && (
        <FilterCreditDrawerDialog
          selectedCreditScoreId={selectedCreditScoreId}
          selectedIsOver25={selectedAge}
          creditScores={creditScores}
          ageRanges={ageRanges}
          selectedAgeRangeId={selectedAgeRangeId}
          open={creditOpen}
          error={creditError}
          handleClose={creditHandleClose}
          handleOpen={creditHandleOpen}
          pillClassName={pillClassName}
          dark={false}
          forceShorthandCopy={true}
          alreadyQualified={alreadyQualified}
          ageRangePlaceholder={ageRangePlaceholder}
          creditScorePlaceholder={creditScorePlaceholder}
        />
      )}

      {showPlanFilter ? (
        <FilterPlanDrawerDialog
          handleClose={planHandleClose}
          open={planOpen}
          handleOpen={planHandleOpen}
          error={planError}
          mileagePlans={mileagePlans}
          selectedPlanMileage={selectedPlanMileage}
          fullstoryEventDelegate={fullstoryEventDelegate}
          pillClassName={pillClassName}
          dark={false}
          showShorthandCopy={false}
        />
      ) : null}

      {showInsuranceFilter ? (
        <FilterPillSelect
          options={options}
          text={`Protection: ${selectedInsuranceTier.name}`}
          icon={<Insurance />}
          pillClassName={pillClassName}
          dark={false}
          defaultValue={selectedInsuranceTier.id}
          onValueChange={submitInsuranceTier}
          name="insurance_tier"
          disabled={!showInsuranceFilter}
          selectContentClassName="w-[232px]"
        />
      ) : null}
    </>
  );
}
